import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { staffApi, errMsg } from "@/lib/api";
import { INTENTS, intentById } from "@/lib/intents";
import { RequestLog } from "@/components/staff/RequestLog";

const Stat = ({ item, count }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`visitor-stat-${item.id}`}>
    <p className="text-xs font-medium text-slate-500">{item.short}</p>
    <p className="mt-1 font-display text-3xl font-bold" style={{ color: item.color }}>{count}</p>
  </div>
);

export const VisitorLog = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { staffApi().get("/visitors").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const counts = useMemo(() => Object.fromEntries(INTENTS.map((i) => [i.id, rows.filter((r) => r.intent === i.id).length])), [rows]);
  const contacts = rows.filter((r) => r.name);

  const setStatus = async (r, status) => {
    try { await staffApi().patch(`/visitors/${r.id}`, { status }); setRows(rows.map((x) => (x.id === r.id ? { ...x, status } : x))); toast.success("Follow-up status updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="visitor-log">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Why visitors come</h2>
        <p className="text-sm text-slate-500">{rows.length} questionnaire answers · {contacts.length} asked to be contacted</p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">{INTENTS.map((i) => <Stat key={i.id} item={i} count={counts[i.id]} />)}</div>
      <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Intent</TableHead><TableHead>Contact</TableHead><TableHead>Message</TableHead><TableHead>Follow-up</TableHead></TableRow></TableHeader>
          <TableBody>
            {contacts.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-400">No contact requests yet.</TableCell></TableRow>}
            {contacts.map((r) => {
              const it = intentById(r.intent);
              return (
                <TableRow key={r.id} data-testid={`visitor-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{format(parseISO(r.created_at), "MMM d, h:mm a")}</TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell><span className="whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: `${it.color}14`, color: it.color }}>{it.short}</span></TableCell>
                  <TableCell className="text-xs">{r.email}<br />{r.phone}</TableCell>
                  <TableCell className="max-w-[260px] text-xs text-slate-600">{r.message}</TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                      <SelectTrigger className="h-8 w-[130px]" data-testid={`visitor-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white"><SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem><SelectItem value="connected">Connected</SelectItem></SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <RequestLog />
    </div>
  );
};
